/**
 * UPDATE FROM THE SITE MEASURE — the same server rule the iPad uses (contracts/proposal-from-measure.ts).
 *
 * Jacky, 18 Oct 2026: *"measure replaces what it owns, my Proposal edits stay, show me the change list to
 * confirm"*. One button asks the server what the latest saved measure would change (the newest save, not the one
 * the Proposal was linked to — the move is the first line of the list); nothing is written until
 * the list is accepted, and then it is saved through `save_customer_proposal` with the measure linked.
 */
import {useState} from 'react';
import {Button} from '@aiw/ui';
import {authHeaders, fetchImpl} from '../lib/api';
import {env} from '../lib/env';

type Change = {kind: 'add' | 'change' | 'remove'; text: string};
type Save = {owner_name: string | null; save_number: number | null; saved_at: string | null};
/** `from_revision` is the save the Proposal was linked to, when the refresh moves it to a newer one. */
type Preview = {proposal_id: string | null; version: number; changes: Change[]; binding: {revision_id: string} | null; revision: Save; from_revision?: Save | null};

export function MeasureUpdate({proposalId, leadId, jobRef, revisionId, dirty, onSaveFirst, onApplied}: {
  proposalId: string | null; leadId: string | null; jobRef: string | null; revisionId?: string | null;
  /** Unsaved edits on screen: the server builds from the SAVED Proposal, so they are saved first. */
  dirty: boolean; onSaveFirst: () => Promise<unknown>;
  onApplied: (proposalId: string) => void;
}) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const target = proposalId ? {proposal_id: proposalId} : leadId ? {lead_id: leadId} : jobRef ? {job_ref: jobRef} : null;
  if (!target) return null;

  const call = async (apply: boolean) => {
    // Apply pins the save that was previewed, so a save landing in between is never applied unseen.
    const save = (apply ? preview?.binding?.revision_id : null) ?? revisionId;
    const r = await fetchImpl(`${env.apiBase}/api/proposals/from-measure`, {method: 'POST',
      headers: {...(await authHeaders()), 'content-type': 'application/json'},
      body: JSON.stringify({...target, ...(save ? {revision_id: save} : {}), apply,
        ...(apply && preview ? {expected_version: preview.version, idempotency_key: crypto.randomUUID()} : {})})});
    const b = await r.json().catch(() => ({}));
    if (!r.ok) throw Error(b.error ?? b.reason ?? 'The site measure could not be applied');
    return b;
  };
  const ask = async () => {
    setBusy(true); setError('');
    try {
      if (dirty) await onSaveFirst();
      setPreview((await call(false)).data as Preview);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true); setError('');
    try {
      const b = await call(true);
      if (b.status !== 'executed') throw Error(b.reason ?? 'The update needs review before it is saved');
      setPreview(null);
      onApplied(String(b.result?.id ?? proposalId));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const mark = {add: '+', change: '~', remove: '−'} as const;
  return <section className="rounded-lg border border-l-4 border-l-primary bg-card p-4 space-y-2">
    <header className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="font-display text-sm font-semibold">Update from the site measure</h2>
      {!preview && <Button size="sm" disabled={busy} onClick={() => void ask()}>{busy ? 'Reading…' : 'Check what changes'}</Button>}
    </header>
    <p className="text-sm text-muted-foreground">The measure replaces what it owns — rooms, plan, products and options from the iPad, underlay and trims. Your wording, prices and lines you added stay.</p>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {preview && <div className="space-y-2">
      <p className="text-sm font-medium">
        {preview.from_revision ? `Save ${preview.from_revision.save_number ?? '?'} → ` : ''}
        {preview.revision.owner_name ? `Save ${preview.revision.save_number} by ${preview.revision.owner_name}` : 'Latest saved measure'}
        {preview.revision.saved_at ? ` · ${new Date(preview.revision.saved_at).toLocaleString('en-AU', {timeZone: 'Australia/Sydney'})}` : ''}
      </p>
      {preview.changes.length === 0
        ? <p className="text-sm">Nothing changes — the Proposal already matches this measure.</p>
        : <ul className="max-h-72 space-y-1 overflow-auto text-sm">{preview.changes.map((c, i) => <li key={i} className={c.kind === 'remove' ? 'text-destructive' : c.kind === 'add' ? 'text-primary' : ''}>
            <span className="num mr-2 inline-block w-3">{mark[c.kind]}</span>{c.text}</li>)}</ul>}
      <div className="flex gap-2">
        <Button size="sm" disabled={busy || preview.changes.length === 0} onClick={() => void apply()}>{busy ? 'Saving…' : 'Apply these changes'}</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setPreview(null)}>Cancel</Button>
      </div>
    </div>}
  </section>;
}
